
import { useRef, useState } from 'react';

const sections = [
  { id: 'introduction', title: 'Introduction' },
  { id: 'acceptance', title: 'Acceptance of Terms' },
  { id: 'user-accounts', title: 'User Accounts' },
  { id: 'use-of-services', title: 'Use of Services' },
  { id: 'prohibited-activities', title: 'Prohibited Activities' },
  { id: 'user-generated-content', title: 'User Generated Content' },
  { id: 'intellectual-property', title: 'Intellectual Property' },
  { id: 'data-protection', title: 'Data Protection & Privacy' },
  { id: 'payment-terms', title: 'Payment Terms' },
  { id: 'service-availability', title: 'Service Availability & SLA' },
  { id: 'warranties', title: 'Warranties & Disclaimers' },
  { id: 'termination', title: 'Termination' },
  { id: 'liability', title: 'Limitation of Liability' },
  { id: 'indemnification', title: 'Indemnification' },
  { id: 'force-majeure', title: 'Force Majeure' },
  { id: 'third-party-links', title: 'Third-Party Links & Content' },
  { id: 'accessibility', title: 'Accessibility' },
  { id: 'export-controls', title: 'Export Controls' },
  { id: 'dispute-resolution', title: 'Dispute Resolution' },
  { id: 'governing-law', title: 'Governing Law' },
  { id: 'severability', title: 'Severability' },
  { id: 'entire-agreement', title: 'Entire Agreement' },
  { id: 'updates', title: 'Updates & Modifications' },
  { id: 'contact', title: 'Contact Information' },
];

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState('');
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const handleScrollTo = (id: string) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50 font-sans">
      <div className="w-full md:w-72 bg-white shadow-lg md:h-screen md:sticky md:top-0 overflow-y-auto p-6 border-r border-gray-200 hidden md:block scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        <h3 className="text-lg font-bold text-gray-800 mb-4 px-2">Table of Contents</h3>
        <div className="space-y-1">
          {sections.map((section) => (
            <div
              key={section.id}
              className={`cursor-pointer py-2 px-3 rounded-md text-sm font-medium transition-colors duration-200 ${activeSection === section.id ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-600' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
              onClick={() => handleScrollTo(section.id)}
            >
              {section.title}
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 p-6 md:p-12 max-w-5xl mx-auto [&_a]:text-blue-600 [&_a]:underline [&_a]:font-medium hover:[&_a]:text-blue-800 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:space-y-2 [&_li]:text-gray-700">
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-8 text-center border-b pb-6">Terms of Service for Sonoma Cloud</h1>

        <div id="introduction" ref={el => { sectionRefs.current['introduction'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">Introduction</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Welcome to Sonoma Cloud ("Sonoma", "we", "our", or "us"). These Terms of Service ("Terms") govern your access to and use of our platform, services, and applications. By accessing or using Sonoma, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you must not use our services.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Sonoma Cloud is designed to provide comprehensive cloud-based solutions for educational institutions, businesses, and organizations, including school management, data processing, and collaborative services in compliance with Kenyan laws and regulations.
          </p>
        </div>

        <div id="acceptance" ref={el => { sectionRefs.current['acceptance'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">Acceptance of Terms</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            By registering for, accessing, or using Sonoma, you acknowledge that you have read, understood, and agree to be bound by these Terms. This agreement constitutes a legally binding contract between you and Sonoma under the laws of Kenya.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            If you are entering into this agreement on behalf of an educational institution or organization, you represent that you have the authority to bind that entity to these Terms.
          </p>
        </div>

        <div id="user-accounts" ref={el => { sectionRefs.current['user-accounts'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">User Accounts</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            To access certain features of Sonoma, you must create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            You must be at least 18 years old or have parental/guardian consent to create an account, in compliance with the <a href="https://www.nationallaws.org/kenya/childrens-act" target="_blank" rel="noopener noreferrer">Kenya Children's Act</a>.
          </p>
        </div>

        <div id="use-of-services" ref={el => { sectionRefs.current['use-of-services'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">Use of Services</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Sonoma grants you a limited, non-exclusive, non-transferable license to access and use our services for lawful educational purposes only. You may not use our services for any illegal or unauthorized purpose.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Our services include but are not limited to:
          </p>
          <ul className="text-gray-700 mb-4 leading-relaxed text-base">
            <li>Student information management</li>
            <li>Academic record tracking</li>
            <li>Attendance monitoring</li>
            <li>Communication tools</li>
            <li>Financial management features</li>
          </ul>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            You agree to use our services in compliance with all applicable laws, including the <a href="https://www.odpc.go.ke/download/data-protection-act-2019/" target="_blank" rel="noopener noreferrer">Kenya Data Protection Act, 2019</a> and the <a href="https://ca.go.ke/the-information-and-communications-act/" target="_blank" rel="noopener noreferrer">Kenya Information and Communications Act</a>.
          </p>
        </div>

        <div id="user-generated-content" ref={el => { sectionRefs.current['user-generated-content'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">User Generated Content</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            You may be permitted to post, upload, or submit content to Sonoma ("User Content"). You retain ownership of your User Content, but by submitting it, you grant Sonoma a worldwide, non-exclusive, royalty-free license to use, display, reproduce, modify, and distribute your User Content in connection with operating and providing our services.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            You represent and warrant that:
          </p>
          <ul className="text-gray-700 mb-4 leading-relaxed text-base">
            <li>You own or have the necessary rights to use and authorize us to use your User Content</li>
            <li>Your User Content does not violate any laws, third-party rights, or these Terms</li>
            <li>Your User Content is accurate and not misleading</li>
            <li>Your User Content does not contain viruses, malware, or harmful code</li>
          </ul>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            We reserve the right to remove or modify any User Content that violates these Terms or applicable laws, including the <a href="https://kenyalaw.org/kl/fileadmin/pdfdownloads/Acts/ComputerMisuseandCybercrimesAct2018.pdf" target="_blank" rel="noopener noreferrer">Kenya Computer Misuse and Cybercrimes Act, 2018</a>.
          </p>
        </div>

        <div id="intellectual-property" ref={el => { sectionRefs.current['intellectual-property'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">Intellectual Property</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            All content, features, and functionality of Sonoma, including but not limited to software, trademarks, logos, and databases, are owned by Sonoma or our licensors and are protected by Kenyan and international intellectual property laws.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            You may not reproduce, distribute, modify, or create derivative works of our intellectual property without prior written consent. Any unauthorized use may constitute infringement under the <a href="https://kenyalaw.org/kl/fileadmin/pdfdownloads/Acts/CopyrightAct.pdf" target="_blank" rel="noopener noreferrer">Kenya Copyright Act</a>.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            User-generated content remains the property of the user, but by uploading it to Sonoma, you grant us a license to use, display, and distribute it as necessary for service provision.
          </p>
        </div>

        <div id="data-protection" ref={el => { sectionRefs.current['data-protection'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">Data Protection & Privacy</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Your privacy is important to us. Our collection and use of personal data is governed by our Privacy Policy and the <a href="https://www.odpc.go.ke/download/data-protection-act-2019/" target="_blank" rel="noopener noreferrer">Kenya Data Protection Act, 2019</a>. By using Sonoma, you consent to such data practices.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            We implement appropriate technical and organizational measures to protect your data, including encryption, access controls, and regular security audits.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            You are responsible for ensuring that any personal data you upload complies with applicable data protection laws.
          </p>
        </div>

        <div id="warranties" ref={el => { sectionRefs.current['warranties'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">Warranties & Disclaimers</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Sonoma provides our services "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            We warrant that:
          </p>
          <ul className="text-gray-700 mb-4 leading-relaxed text-base">
            <li>Our services will perform substantially in accordance with our published specifications</li>
            <li>We have the right to provide the services to you</li>
            <li>Our services comply with applicable Kenyan data protection and privacy laws</li>
          </ul>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            We do not warrant that our services will be error-free, uninterrupted, or free from security threats. We disclaim all liability for any damages arising from your use of third-party services or integrations.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Educational outcomes or academic results achieved through our platform are not guaranteed and depend on various factors including user implementation and institutional practices.
          </p>
        </div>

        <div id="export-controls" ref={el => { sectionRefs.current['export-controls'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">Export Controls</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            You acknowledge that Sonoma and its underlying technology may be subject to export control laws and regulations of Kenya and other jurisdictions. You agree not to export, re-export, or transfer Sonoma or any related technology in violation of applicable export control laws.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            This includes but is not limited to:
          </p>
          <ul className="text-gray-700 mb-4 leading-relaxed text-base">
            <li>Kenyan export control regulations</li>
            <li>United Nations sanctions and embargoes</li>
            <li>International trade restrictions</li>
            <li>Technology transfer controls</li>
          </ul>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            You represent and warrant that you are not located in, under the control of, or a national or resident of any restricted country or territory, and that you are not on any restricted party list.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Educational institutions must ensure compliance with export control requirements when using Sonoma for international student programs or cross-border educational activities.
          </p>
        </div>

        <div id="liability" ref={el => { sectionRefs.current['liability'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">Limitation of Liability</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            To the fullest extent permitted by law, Sonoma shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our services.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Our total liability shall not exceed the amount paid by you for the services in the 12 months preceding the claim.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            This limitation is subject to the <a href="https://kenyalaw.org/kl/fileadmin/pdfdownloads/Acts/Cap.23LawofContractAct.pdf" target="_blank" rel="noopener noreferrer">Kenya Law of Contract Act</a>.
          </p>
        </div>

        <div id="dispute-resolution" ref={el => { sectionRefs.current['dispute-resolution'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">Dispute Resolution</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Any disputes arising from these Terms shall be resolved through negotiation, arbitration, or Kenyan courts as appropriate.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            We encourage amicable resolution first. Disputes may be escalated to the <a href="https://www.odpc.go.ke/" target="_blank" rel="noopener noreferrer">Office of the Data Protection Commissioner</a> where data protection issues are involved.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Arbitration shall be conducted under the <a href="https://kenyalaw.org/kl/fileadmin/pdfdownloads/Acts/ArbitrationAct.pdf" target="_blank" rel="noopener noreferrer">Kenya Arbitration Act</a>.
          </p>
        </div>

        <div id="entire-agreement" ref={el => { sectionRefs.current['entire-agreement'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">Entire Agreement</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            These Terms, together with our Privacy Policy and any other legal notices published by us on our platform, constitute the entire agreement between you and Sonoma regarding the use of our services.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            This agreement supersedes all prior agreements, understandings, or representations, whether written or oral, relating to the subject matter hereof.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Any amendments or modifications to these Terms must be in writing and signed by both parties, except as otherwise provided herein.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            In the event of any conflict between these Terms and any other agreement or document, these Terms shall prevail unless expressly stated otherwise.
          </p>
        </div>

        <div id="updates" ref={el => { sectionRefs.current['updates'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">Updates & Modifications</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            We reserve the right to modify these Terms at any time. Changes will be effective upon posting to our platform.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            Continued use of Sonoma after changes constitutes acceptance of the modified Terms.
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            We will notify users of material changes via email or platform notifications.
          </p>
        </div>

        <div id="contact" ref={el => { sectionRefs.current['contact'] = el; }}>
          <h2 className="text-2xl font-bold text-gray-800 mt-10 mb-4 border-b border-gray-200 pb-2">Contact Information</h2>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            If you have any questions about these Terms, please contact us:
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed text-base">
            <strong>Email:</strong> otienonickolas9@gmail.com <br />
            <strong>Phone:</strong> +254 743 993 561 <br />
            <strong>Postal Address:</strong> 00100, Nairobi, Kenya
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;